import * as React from "react";

import { getStore } from "bundles/BigqueryQueryViewer/store";
import { observer } from "mobx-react-lite";
import { ContextMenuItem, Item } from "../TreeView/types";
import { TreeView } from "../TreeView";
import { useEffect } from "react";
import { SavedQuery } from "../SavedQuery";
import { SharedQuery } from "../SharedQuery";
import { IDockviewPanelProps } from "dockview";
import I18n from "lib/I18n";
import { usePublisher } from "bundles/BigqueryQueryViewer/lib/pubsub";

export const Explorer = observer((props: IDockviewPanelProps) => {
  const store = getStore().explorer;
  const rootStore = getStore();
  const publisher = usePublisher();

  useEffect(() => {
    store.list();
  }, []);

  const onClick = (item: Item) => {
    const isExpanded = store.expandedIds.includes(item.id);
    if (!isExpanded && (item.items?.length ?? 0) > 0) {
      store.expandedIds.push(item.id);
    }
    if (item.id.startsWith("saved_query")) {
      const splits = item.id.split("/");
      const savedQuery = store.savedQuery.savedQueries.find(
        (_) => _.id.toString() == splits[1],
      );
      if (!savedQuery) {
        return;
      }
      const formStore = rootStore.forms.find((_) => _.savedQuery == savedQuery);
      if (formStore) {
        publisher.publish(formStore, "focus");
        return;
      }
      // App/index.tsxでdockview側には同期される
      rootStore.createFormStoreFromSavedQuery(savedQuery);
    } else if (item.id.startsWith("shared_query")) {
      const splits = item.id.split("/");
      const sharedQuery = store.sharedQuery.sharedQueries.find(
        (_) => _.id.toString() == splits[1],
      );
      if (!sharedQuery) {
        return;
      }
      const formStore = rootStore.forms.find(
        (_) => _.sharedQuery == sharedQuery,
      );
      if (formStore) {
        publisher.publish(formStore, "focus");
        return;
      }
      // App/index.tsxでdockview側には同期される
      rootStore.createFormStoreFromSharedQuery(sharedQuery);
    } else {
      const splits = item.id.split("/");
      const dataset = store.datasets.find((_) => _.name == splits[0]);
      const table = (dataset?.tables ?? []).find((_) => _.name == splits[1]);
      if (table) {
        const panel = props.containerApi.panels.find((_) => _.id == item.id);
        if (panel) {
          panel.focus();
          return;
        }
        props.containerApi.addPanel({
          id: item.id,
          title: table.name,
          component: "table",
          position: {
            referenceGroup: "editors",
          },
          params: { dataset, table },
        });
        return;
      }
      const func = (dataset?.funcs ?? []).find((_) => _.name == splits[1]);
      if (func) {
        const panel = props.containerApi.panels.find((_) => _.id == item.id);
        if (panel) {
          panel.focus();
          return;
        }
        props.containerApi.addPanel({
          id: item.id,
          title: func.name,
          component: "func",
          position: {
            referenceGroup: "editors",
          },
          params: { dataset, func },
        });
        return;
      }
    }
  };

  const onToggle = (item: Item, isExpanded: boolean) => {
    if (isExpanded) {
      store.expandedIds.push(item.id);
    } else {
      const index = store.expandedIds.findIndex((_) => _ == item.id);
      if (index >= 0) {
        store.expandedIds.splice(index, 1);
      }
    }
  };

  const contextMenuItems = (item: Item) => {
    if (item.id.startsWith("saved_query/")) {
      return [
        { id: "rename", name: I18n.t("bigquery_query_viewer.label.rename") },
        { id: "delete", name: I18n.t("bigquery_query_viewer.label.delete") },
      ];
    } else if (item.id.startsWith("shared_query/")) {
      const ret: ContextMenuItem[] = [];
      const sharedQuery = store.sharedQuery.sharedQueries.find(
        (_) => _.id.toString() == item.id.split("/")[1],
      );
      if (sharedQuery?.updatable) {
        ret.push({
          id: "rename",
          name: I18n.t("bigquery_query_viewer.label.rename"),
        });
      }
      if (sharedQuery?.deletable) {
        ret.push({
          id: "delete",
          name: I18n.t("bigquery_query_viewer.label.delete"),
        });
      }
      return ret;
    }
    return [];
  };

  const onContextMenu = (item: Item, contextMenuItem: ContextMenuItem) => {
    switch (contextMenuItem.id) {
      case "rename":
        item.isRenaming = true;
        break;
      case "delete":
        if (item.id.startsWith("saved_query")) {
          const savedQuery = rootStore.explorer.savedQuery.savedQueries.find(
            (_) => _.id.toString() == item.id.split("/")[1],
          );
          if (savedQuery) {
            rootStore.explorer.savedQuery.selectedSavedQuery = savedQuery;
            rootStore.explorer.savedQuery.isDeleteOpen = true;
          }
        } else if (item.id.startsWith("shared_query")) {
          const sharedQuery = rootStore.explorer.sharedQuery.sharedQueries.find(
            (_) => _.id.toString() == item.id.split("/")[1],
          );
          if (sharedQuery) {
            rootStore.explorer.sharedQuery.selectedSharedQuery = sharedQuery;
            rootStore.explorer.sharedQuery.isDeleteOpen = true;
          }
        }
        break;
    }
  };

  const onRenameItem = async (item: Item, newName: string) => {
    if (item.id.startsWith("saved_query")) {
      const id = item.id.split("/")[1];
      const savedQuery = store.savedQuery.savedQueries.find(
        (_) => _.id.toString() == id,
      );
      if (savedQuery && (await store.renameSavedQuery(savedQuery, newName))) {
        item.label = newName;
        item.isRenaming = false;
      }
    } else if (item.id.startsWith("shared_query")) {
      const id = item.id.split("/")[1];
      const sharedQuery = store.sharedQuery.sharedQueries.find(
        (_) => _.id.toString() == id,
      );
      if (
        sharedQuery &&
        (await store.renameSharedQuery(sharedQuery, newName))
      ) {
        item.label = newName;
        item.isRenaming = false;
      }
    }
  };

  return (
    <>
      <TreeView
        items={store.items}
        expandedIds={store.expandedIds}
        contextMenuItems={contextMenuItems}
        onItemClick={onClick}
        onToggleExpanstion={onToggle}
        onContextMenu={onContextMenu}
        onRenameItem={onRenameItem}
      />
      <SavedQuery />
      <SharedQuery />
    </>
  );
});
